import React, { useEffect, useState } from 'react';
import { ChevronLeftIcon } from '@chakra-ui/icons';
import {
  Flex,
  IconButton,
  Link,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Text,
} from '@chakra-ui/react';
import { useSelector } from 'react-redux';

import { Ticket, ZendeskUser } from 'clipsal-cortex-types/src/api/api-zendesk';
import { DialogProps } from 'clipsal-cortex-types/src/common/chakra-extension-types';
import { CenteredLoader } from 'clipsal-cortex-ui/src/components/CenteredLoader';
import { ENERGY_MASTERS_TENANT_ID } from 'clipsal-cortex-utils/src/constants/common-constants';
import { useInterval } from 'clipsal-cortex-utils/src/hooks/use-interval';

import { get } from '../../common/api/api-helpers';
import {
  ENERGY_MASTERS_CONTACT_US_LINK,
  ENERGY_MASTERS_EMAIL,
  ENERGY_MASTERS_FAQ_LINK,
  ENERGY_MASTERS_PHONE,
} from '../../common/constants';
import { selectSite } from '../site/siteSlice';
import { Home } from './Home';
import { SubmitTicket } from './SubmitTicket';
import { ViewTicket } from './ViewTicket';

export type SupportRoute = 'home' | 'submitTicket' | 'viewTicket';

const ROUTE_TO_TITLE: Record<SupportRoute, string> = {
  home: 'Support Center',
  submitTicket: 'New Ticket',
  viewTicket: 'View Ticket',
};

type SupportModalProps = { defaultRoute?: SupportRoute } & DialogProps;

export function SupportModal({ isOpen, onClose, defaultRoute = 'home' }: SupportModalProps) {
  const site = useSelector(selectSite);
  const isEnergyMastersSite = site.tenant_id === ENERGY_MASTERS_TENANT_ID;

  return isEnergyMastersSite ? (
    <SupportModalEnergyMasters {...{ isOpen, onClose, defaultRoute }} />
  ) : (
    <SupportModalClipsalSolar {...{ isOpen, onClose, defaultRoute }} />
  );
}

export function SupportModalClipsalSolar({ isOpen, onClose, defaultRoute = 'home' }: SupportModalProps) {
  const [isLoaded, setLoaded] = useState(false);
  const [route, setRoute] = useState<SupportRoute>(defaultRoute);
  const [zendeskUser, setZendeskUser] = useState<ZendeskUser | null>(null);
  const [currentlyViewingTicket, setCurrentlyViewingTicket] = useState<Ticket | null>(null);

  const routeToContents: Record<SupportRoute, JSX.Element> = {
    home: <Home onChangeCurrentlyViewingTicket={setCurrentlyViewingTicket} onChangeRoute={setRoute} />,
    submitTicket: (
      <SubmitTicket
        zendeskUser={zendeskUser}
        onChangeUser={setZendeskUser}
        onChangeCurrentlyViewingTicket={(ticket) => {
          setCurrentlyViewingTicket(ticket);
          setRoute('viewTicket');
        }}
      />
    ),
    viewTicket: <ViewTicket zendeskUser={zendeskUser} ticket={currentlyViewingTicket} />,
  };

  useEffect(() => {
    async function fetchTicketFields() {
      try {
        const zendeskUser = await get<ZendeskUser>(`/v1/zendesk_user`);
        setZendeskUser(zendeskUser);
      } catch (e) {
        console.info('No user in Zendesk, one will be created when a ticket is created.');
      }

      setLoaded(true);
    }

    fetchTicketFields();
  }, [setLoaded]);

  useInterval(async () => {
    if (isLoaded && !zendeskUser) {
      try {
        const zendeskUser = await get<ZendeskUser>(`/v1/zendesk_user`);
        setZendeskUser(zendeskUser);
      } catch (e) {
        console.info('Still no Zendesk user, trying again in 5 seconds.');
      }
    }
  }, 5000);

  return (
    <Modal scrollBehavior="inside" isCentered={true} size={'xl'} isOpen={isOpen} onClose={onClose}>
      <ModalOverlay />
      <ModalContent data-testid="support-center-modal" rounded={20}>
        <ModalHeader>
          <Flex align={'center'}>
            {route !== 'home' && (
              <IconButton
                onClick={() => setRoute('home')}
                mr={1}
                size={'sm'}
                variant={'ghost'}
                aria-label="Go back"
                icon={<ChevronLeftIcon w={'20px'} h={'20px'} />}
              />
            )}
            {ROUTE_TO_TITLE[route]}
          </Flex>
        </ModalHeader>
        <ModalCloseButton data-testid="support-center-close-btn" />
        <ModalBody pb={6}>{isLoaded ? routeToContents[route] : <CenteredLoader />}</ModalBody>
      </ModalContent>
    </Modal>
  );
}

export function SupportModalEnergyMasters({ isOpen, onClose }: SupportModalProps) {
  return (
    <Modal scrollBehavior="inside" isCentered={true} size={'xl'} isOpen={isOpen} onClose={onClose}>
      <ModalOverlay />
      <ModalContent data-testid="support-center-modal-energy-masters" rounded={20} mx={4}>
        <ModalHeader>
          <Flex align={'center'}>Contact Energy Masters Support</Flex>
        </ModalHeader>
        <ModalCloseButton data-testid="support-center-close-btn" />
        <ModalBody pb={6} pt={0}>
          <Text>
            You can reach Energy Masters support by calling{' '}
            <Link
              href={`tel:${ENERGY_MASTERS_PHONE.replace(/\s/g, '')}`}
              style={{ textDecoration: 'none' }}
              color="customBlue.500"
            >
              {ENERGY_MASTERS_PHONE}
            </Link>{' '}
            or emailing{' '}
            <Link href={`mailto:${ENERGY_MASTERS_EMAIL}`} style={{ textDecoration: 'none' }} color="customBlue.500">
              {ENERGY_MASTERS_EMAIL}
            </Link>{' '}
            (9am - 4:30pm Monday to Friday, excluding public holidays).
          </Text>

          <Text mt={4}>
            Your question may also be addressed on the{' '}
            <Link href={ENERGY_MASTERS_CONTACT_US_LINK} style={{ textDecoration: 'none' }} color="customBlue.500">
              Energy Masters website
            </Link>
            . For frequently asked questions about Energy Masters, please refer{' '}
            <Link href={ENERGY_MASTERS_FAQ_LINK} style={{ textDecoration: 'none' }} color="customBlue.500">
              here
            </Link>
            .
          </Text>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
}
